import ajv from '~/lib/ajv'
import { conformModulators } from '~/lib/automation/model'
import { isFutureSettings, migrateSettings, SETTINGS_VERSION } from '~/lib/migrations'
import { quantize, settleSourceSettings } from '~/lib/params'
import { EFFECT_SCHEMAS, pruneSection } from '~/lib/presetSettings'
import { display_object_schema } from '~/schemas/display_object_schema'

// A clamped or dropped value is a SILENT change to what the user pasted, so it says so. `warn`, not `error`: the paste succeeded, and partial credit is the intended outcome.
const logger = console.scope('conform-settings', true)

/**
 * Bring one VALUE inside the domain its param declares. Runs after coercion, so the value is already the right type and only its magnitude or membership can still be wrong.
 *
 * ⚠️ CLAMP AND SUBSTITUTE, never reject. A pasted preset is something the user asked for; failing the whole thing over one bad `feather` loses six good effects to fix one number. This mirrors the server's own rule that a validation failure discards only the offending effect.
 *
 * `enum` falls back to `initial` because a closed set has no nearest neighbour — an unrecognised option is not "too high", it is not a member, so there is nothing to clamp toward. ⚠️ NO EFFECT PARAM DECLARES ONE TODAY (`inpaint.segmentation_class` was the last and left the contract), so this branch is currently unreachable from stored settings. It stays because the rule is the enum's, not that param's: the next closed set to land must not silently keep a value outside it.
 */
export function conformValue(key, val, prop, where) {
	if (prop.enum && !prop.enum.includes(val)) {
		logger.warn('conformSettings', `${where}.${key}: '${val}' is not a declared option — using the default`)
		return prop.initial
	}
	if (typeof val === 'number') {
		// Rounded to the precision `step` declares BEFORE the range tests, so a clamp judges the value that will actually be stored.
		const q = quantize(prop, val)
		const { minimum: lo, maximum: hi } = prop
		if (typeof lo !== 'number' || typeof hi !== 'number') return q
		// ⚠️ A CYCLIC PARAM WRAPS, IT NEVER CLAMPS — that is what `wrap` declares, and rotation is the case that proves it: a stored `-0.59°` is 359.41°, and clamping it to 0 would be a visible jump on a shape the user never touched. `quantize` has already folded it.
		if (prop.wrap) {
			// Only an actual wrap is news; step-rounding a fractional angle happens on every stored shape.
			if (val < lo || val > hi) logger.warn('conformSettings', `${where}.${key}: ${val} → ${q}`)
			return q
		}
		if (q >= lo && q <= hi) return q
		const clamped = q < lo ? lo : hi
		logger.warn('conformSettings', `${where}.${key}: ${val} is outside ${lo}..${hi} — clamped to ${clamped}`)
		return clamped
	}
	return val
}

/**
 * COERCE, then BOUND, then PRUNE.
 *
 * ⚠️ ABSENCE IS THE ONLY SPELLING OF AT-INITIAL, both directions: it never fills a key the input omits, and it drops one the input holds at its initial. `selectPresetChanged` diffs on key existence and every save strips the stored side, so a dense legacy preset reads permanently dirty otherwise.
 *
 * ⚠️ Coercion FIRST, so a string `'250'` becomes 250 and clamps to 100 rather than being read as a non-number and reset to `initial`.
 */
function conformSection(data, schema, where) {
	const input = data && typeof data === 'object' ? data : {}
	// ⚠️ An effect's params are a CLOSED vocabulary here, so an undeclared key is DROPPED: a leaked `_v` rides to the wire, where an unknown field fails a strict Pydantic model and the rejected message discards the whole parameter state. ⚠️ Dropping an EFFECT is the expensive failure the loop below prevents.
	return pruneSection(input, schema.properties, {
		dropUndeclared: true,
		transform: (key, val, prop) => {
			let coerced
			// `integer` alongside `number`: both are numeric on the wire, and a param that falls past this branch lands in the raw passthrough with no coercion at all — the opposite of what a stricter type should get.
			if (prop.type === 'number' || prop.type === 'integer') {
				coerced = typeof val === 'number' && Number.isFinite(val) ? val : (prop.initial ?? 0)
			} else if (prop.type === 'boolean') {
				coerced = typeof val === 'boolean' ? val : (prop.initial ?? false)
			} else if (prop.type === 'string') {
				coerced = typeof val === 'string' ? val : (prop.initial ?? '')
			} else {
				coerced = val
			}
			return conformValue(key, coerced, prop, where)
		},
	})
}

/**
 * The scene graph a pasted preset carries, validated per instance.
 *
 * ⚠️ THE SAME DATA PASTED TWO WAYS MUST SURVIVE THE SAME CHECKS. `pasteAction` runs full Ajv over a pasted OBJECT (`display_objects_array_schema`) and rejects `opacity: 500`; a preset carries objects too, and this half used to be a raw copy — so the identical rect landed in `live` unvalidated, reached Pixi on every frame, and rode the wire to a server that does not validate objects inbound either.
 *
 * PER INSTANCE, clamped where a value can be repaired and DROPPED only where it cannot — the same partial-credit rule the effects follow above. An object still failing after the clamp is one the client could not render anyway; the alternative is discarding the whole scene for one bad field.
 *
 * ⚠️ Validated against the type's OWN definition, not the `oneOf` union: the union reports "matched no branch", which names neither the type nor the field. Per-type Ajv errors name the property that failed.
 *
 * ⚠️ `required` IS STRIPPED, and that is the whole difference between this and the pasted-object path. A stored object is SPARSE — absence means at initial, and `updateObject` deletes a key the moment its value returns to the default — so a `path` legitimately carries no `width`, and `root` carries only `{id, children}`. Enforcing `required` here judges stored data by the rules for a complete wire instance: measured against the dev fixtures it discards 31 of 125 objects across 25 presets. What this path may check is that a value the object DOES carry is legal; presence is the carrier's business, not the contract's.
 */
const validators = {}
const validatorFor = type => {
	const def = display_object_schema.definitions[type]
	if (!def) return null
	if (!validators[type]) {
		// `required` and the `type` discriminant both describe a COMPLETE WIRE INSTANCE, not stored data. A stored object is sparse, and a stored `root` carries `type: null` (or none) because it is identified by its id — so checking `const: 'root'` here rejects 13 of the fixtures' roots for a field the carrier never had to fill. What remains is what this path may legitimately judge: the VALUES an object does carry.
		//
		// ⚠️ `$id` IS DROPPED WITH THEM. Every definition carries one (`root`, `rect`, …), and Ajv's registry is global to the shared instance — so spreading it into this DERIVED schema claims an id the original already owns and throws `schema with key or id "root" already exists` on the second compile. This validator is anonymous by nature: nothing `$ref`s it, so it needs no id.
		// eslint-disable-next-line no-unused-vars -- `required`/`type`/`$id` describe a registered wire instance, not stored data.
		const {
			required,
			$id,
			properties: { type: _type, ...properties },
			...rest
		} = def
		validators[type] = ajv.compile({ ...rest, properties })
	}
	return validators[type]
}

function conformObjects(objects, faults = []) {
	if (!objects || typeof objects !== 'object') return {}
	const result = {}
	for (const [id, obj] of Object.entries(objects)) {
		if (!obj || typeof obj !== 'object') continue
		// ⚠️ THE ROOT IS IDENTIFIED BY ITS ID, not by a `type` field — stored roots carry `{id, children}` and nothing else, and `live/index.js` exempts `id === 'root'` from the same requirement. Resolving it here by ID is what stops the stage container being read as an unknown type and dropped, which takes the whole scene's parenting with it.
		const validate = validatorFor(id === 'root' ? 'root' : obj.type)
		// A STORED `null` IS AN ABSENT KEY, not a bad value — old presets persist explicit nulls for `fill`/`opacity`/`locked`, which is the same asymmetry `expand` documents and resolves with `??` on the way out. Validating them would reject data every other reader already handles; dropping them here lets `getObjectInitial` resolve each one at its own read.
		const present = Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== null))
		if (!validate) {
			logger.warn('conformSettings', `object '${id}': unknown type '${obj.type}' — dropped`)
			continue
		}
		// CLAMP BEFORE JUDGING, the same order and the same partial-credit rule the effects follow: an out-of-range number is a fixable value, and dropping a whole shape over one (a stored `rotation: -0.59` against a declared floor of 0) would lose the user's geometry to repair a rounding error. Only what remains wrong after clamping is grounds to drop the object.
		const props = display_object_schema.definitions[id === 'root' ? 'root' : obj.type]?.properties ?? {}
		const clamped = {}
		for (const [key, val] of Object.entries(present)) {
			clamped[key] = props[key] ? conformValue(key, val, props[key], `object '${id}'`) : val
		}
		if (!validate(clamped)) {
			logger.warn('conformSettings', `object '${id}' (${obj.type}) failed validation — dropped`, validate.errors)
			continue
		}
		// The clamped values are kept, but the object's OWN nulls are not re-added: a null was only removed for the check, and every reader resolves it through the contract anyway (`getObjectInitial`). Merging over the original preserves keys the contract does not declare — `name`, and the runtime fields `extra: 'allow'` covers server-side.
		const merged = { ...obj, ...clamped }
		// ⚠️ Deleted from the MERGE, not skipped in `clamped` — the spread of the original would otherwise put the dense key straight back.
		for (const key in clamped) {
			if (props[key] && clamped[key] === props[key].initial) delete merged[key]
		}
		result[id] = merged
	}
	repairMaskLinks(result, faults)
	return result
}

/**
 * A mask is a TWO-WAY link: `a.mask === b` iff `b.maskFor === a`. `setObjectMask` writes both halves and `recursivelyDeleteObjects` removes both, so a lone half is not a state the app can author — it is stale or hand-made data.
 *
 * Repaired rather than rejected, like every other fault at this door: a half-link costs the mask, where dropping the object would cost the user's geometry. Both directions are checked, since either half can be the survivor.
 */
function repairMaskLinks(objects, faults) {
	for (const [id, obj] of Object.entries(objects)) {
		// `a.mask` pointing at a missing object, or at one that names someone else.
		if (obj.mask && objects[obj.mask]?.maskFor !== id) {
			faults.push(`object '${id}': mask '${obj.mask}' does not point back`)
			delete obj.mask
		}
		// The mirror: a stencil naming a target that is gone, or that no longer names it.
		if (obj.maskFor && objects[obj.maskFor]?.mask !== id) {
			faults.push(`object '${id}': maskFor '${obj.maskFor}' does not point back`)
			delete obj.maskFor
		}
	}
}

/**
 * THE door into the RTK cache: the four `transformResponse` reads and the two optimistic writes.
 *
 * ⚠️ TRUST IS A PROPERTY OF THE DATA, NOT THE PATH IT ARRIVED BY, so validation is unconditional and `pasteAction` is an ordinary caller. No boundary can tell a 2024 preset from a well-formed one.
 *
 * parse-if-string → migrate → coerce → bound → prune → validate objects.
 *
 * ⚠️ AT-INITIAL HAS ONE SPELLING, ABSENCE, at both levels: a param equal to its `initial` is dropped, and an effect left with no params goes with it. It still never CREATES an effect the input lacks — naming one asserts it exists, and the server ADDS a missing effect on `{}`.
 *
 * ⚠️ A SETTINGS-LEVEL key that is neither an effect, `objects`, `modulators`, `source_settings` nor `_v` is DROPPED — a closed vocabulary whose violation is `playback`, session state a pasted preset must never carry into `live`.
 *
 * Migrate BEFORE conforming: conforming a legacy effect against the current schemas would coerce the old keys away before the relocation ever saw them.
 */
// TODO: cross-user thumbnail resolution not yet supported — thumbnails from other users' presets won't resolve
export default function conform(settings, { strict = false } = {}) {
	let raw = settings
	if (typeof raw === 'string') {
		try {
			raw = JSON.parse(raw)
		} catch (e) {
			// A corrupt preset loads as empty rather than throwing — one unreadable preset must not take the whole project's load down with it.
			logger.error('conform', 'Failed to parse settings', e)
			raw = null
		}
	}
	if (!raw || typeof raw !== 'object') return { _v: SETTINGS_VERSION, objects: {} }

	const migrated = migrateSettings(raw)
	// A future stamp is kept (migrateSettings): conforming must not be what downgrades it.
	const result = { _v: isFutureSettings(migrated) ? migrated._v : SETTINGS_VERSION }
	for (const [key, schema] of Object.entries(EFFECT_SCHEMAS)) {
		if (!(key in migrated)) continue
		// ⚠️ AN EMPTY SECTION IS DROPPED, so at-initial has ONE spelling at every level. No client authors effect presence yet, and the server activates an effect on whether its params are a no-op.
		const section = conformSection(migrated[key], schema, key)
		if (section) result[key] = section
	}
	const faults = []
	result.objects = conformObjects(migrated.objects, faults)
	const modulators = conformModulators(migrated.modulators, result.objects)
	if (modulators) result.modulators = modulators
	const sourceSettings = settleSourceSettings(migrated.source_settings, result.objects)
	if (sourceSettings) result.source_settings = sourceSettings
	if (faults.length) {
		// ⚠️ STRICT IS FOR TESTS AND FIXTURES, never the app. A walk drives real presets through this door, so a fixture the app could not have authored fails the run rather than being quietly repaired into something that no longer tests what it names. In the app the same fault is a repair: one bad preset must not take a project load down.
		if (strict) throw new Error(`conform: invalid settings — ${faults.join('; ')}`)
		for (const f of faults) logger.warn('conformSettings', f)
	}
	return result
}
